import { useEffect, useState } from 'react';
import {
  ChevronRight,
  CrossIcon,
  NoteIcon,
  Pencil,
  Plus,
  Warning,
} from '../../../../../assets/icons';
import UseContext from '../../../../../hooks/useContext';
import Button from '../../../../button';
import useModal from '../../../../modal/useModal';
import ApiDatabase from 'server';
import Tooltip from 'components/tooltips/Tooltips';
import { formatLastUpdate } from 'helper/lastUpdate';
import { isDefaultCoefficientRanges } from 'helper/coefficientRange';
import PostIt, { POST_IT_TITLE_COLOR } from 'components/postIt/PostIt';

const SlideOverCompanyRemunerationConditions = ({
  companyInfo,
  setCompanyInfo,
  setIsShowCoeff,
  setIsCoeffSend,
  onSaved,
}) => {
  const { toggle } = useModal();
  const { setModalData, modalDataReturn, setModalDataReturn } = UseContext();
  const [isCoeffCompleted, setIsCoeffCompleted] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (companyInfo?.coefficients) {
      if (companyInfo?.coefficients?.length > 0) {
        for (let i = 0; i < companyInfo?.coefficients.length; i++) {
          if (
            companyInfo?.coefficients[i]?.management === null ||
            companyInfo?.coefficients[i]?.delegation === null
          ) {
            setIsCoeffCompleted(false);
            break;
          } else {
            setIsCoeffCompleted(true);
            setIsCoeffSend(true);
          }
        }
      } else {
        setIsCoeffCompleted(false);
      }
    } else {
      setIsCoeffCompleted(false);
    }
  }, [companyInfo]);

  const editOvertime = (idCompany) => {
    setModalData({ idCompany });
    toggle('overtime_company');
  };

  const editNightHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('night_hours_company');
  };

  const editSundayHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('sunday_hours_company');
  };

  const editHolidayHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('holiday_hours_company');
  };

  const groupedNightHours = {};
  const groupedOvertimes = {};

  if (companyInfo?.nightHours) {
    companyInfo.nightHours.forEach((nightHour) => {
      // Group by percentage: entries sharing the same rate are listed together.
      const increaseKey = String(nightHour.percentage ?? 'nc');
      if (!groupedNightHours[increaseKey]) {
        groupedNightHours[increaseKey] = {
          items: [],
        };
      }
      groupedNightHours[increaseKey].items.push(nightHour);
    });
  }
  if (companyInfo?.overtimes) {
    companyInfo.overtimes.forEach((overtime) => {
      // Group by percentage: entries sharing the same rate are listed together.
      const increaseKey = String(overtime.percentage ?? 'nc');
      if (!groupedOvertimes[increaseKey]) {
        groupedOvertimes[increaseKey] = {
          items: [],
        };
      }
      groupedOvertimes[increaseKey].items.push(overtime);
    });
  }

  // nightHours/overtimes are arrays Mongoose defaults to [] (never undefined),
  // so a never-configured company still has a truthy, empty array — gate on
  // length rather than on the array's own truthiness.
  const hasNightHours = (companyInfo?.nightHours ?? []).length > 0;
  const hasOvertimes = (companyInfo?.overtimes ?? []).length > 0;

  // sundayHours/holidayHours are sub-documents ({ bonus, percentage }), not booleans:
  // an empty one ({ bonus: null, percentage: null }) is still truthy, so gate on the
  // percentage actually being set rather than on the sub-document's presence.
  const sundayPercentage = companyInfo?.sundayHours?.percentage;
  const hasSundayHours = sundayPercentage !== undefined && sundayPercentage !== null;
  const sundayPercentageLabel = hasSundayHours ? `${sundayPercentage} %` : 'Non défini';

  const holidayPercentage = companyInfo?.holidayHours?.percentage;
  const hasHolidayHours = holidayPercentage !== undefined && holidayPercentage !== null;
  const holidayPercentageLabel = hasHolidayHours ? `${holidayPercentage} %` : 'Non défini';

  // What is stored is a slug ("lundi_de_pentecote"), never a label: the day
  // moves every year and the list itself depends on the company's territory.
  // Only the company's own holiday list can name it, so it is fetched solely
  // when there is a slug to translate on a section that is actually filled.
  const solidarityDay = companyInfo?.holidayHours?.solidarityDay;

  useEffect(() => {
    if (!companyInfo?._id || !hasHolidayHours || !solidarityDay) return;
    ApiDatabase.getHolidays(
      { token, idCompany: companyInfo._id },
      (data) => setHolidays(data?.holidays ?? []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [companyInfo?._id, hasHolidayHours, solidarityDay]);

  // An unresolved slug shows nothing at all: "lundi_de_pentecote" on screen
  // would read as a bug, and the section still carries its percentage.
  const solidarityDayLabel = holidays.find(
    (holiday) => holiday.slug === solidarityDay,
  )?.label;

  // No flag is stored for "still on default values" (D3): infer it per
  // section instead. Sunday/holiday reuse the presence-based gates above
  // rather than truthiness, since their sub-document is always present.
  const isDefault = (section) => {
    switch (section) {
      case 'coefficients':
        // Creation now persists the real template, so an all-null row set is
        // no longer the only default state — compare against the template.
        return isDefaultCoefficientRanges(
          companyInfo?.coefficients,
          companyInfo?.defaultCoefficients
        );
      case 'overtimes':
        return !hasOvertimes;
      case 'nightHours':
        return !hasNightHours;
      case 'sundayHours':
        return !hasSundayHours;
      case 'holidayHours':
        return !hasHolidayHours;
      default:
        return true;
    }
  };

  // Same tooltip and icon for every section; each key falls back to its
  // "ajout_" counterpart when no "modification_" log exists yet.
  const lastUpdateOf = (modificationKey, ajoutKey) =>
    companyInfo?.lastUpdates?.[modificationKey] ?? companyInfo?.lastUpdates?.[ajoutKey];

  // The alert belongs to the right-hand group, beside the edit button — not
  // inline with the label, where it competed with the description text.
  // The icon carries no text of its own, so the tooltip is the only thing that
  // explains it: Tooltip rather than a native title, which the design does not
  // cover and which browsers delay by a second or more.
  const renderDefaultIcon = (section) =>
    isDefault(section) ? (
      <Tooltip text={'Données par défaut'} placement={'top'}>
        <span className={'my-auto'}>
          <Warning wh={20} color={'#975612'} />
        </span>
      </Tooltip>
    ) : null;

  const renderUpdateStatus = (section, lastUpdate, defaultLabel) => {
    if (isDefault(section)) {
      return <div className={'text-gray-500'}>{defaultLabel}</div>;
    }
    // No log entry at all means the company was filled in before logging
    // existed: show nothing extra rather than a stale/undefined date.
    const label = formatLastUpdate(lastUpdate);
    if (!label) return null;
    return <div className={'text-gray-500'}>{label}</div>;
  };

  useEffect(() => {
    // Set once, not incremented: guards against multiple onSaved calls even
    // if a future added type ends up matching more than one branch per pass.
    let didSave = false;

    if (
      modalDataReturn?.type === 'night_hours_updated' &&
      modalDataReturn?.nightHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        nightHours: modalDataReturn.nightHours,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'overtimes_updated' &&
      modalDataReturn?.overtimes
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        overtimes: modalDataReturn.overtimes,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'sunday_hours_updated' &&
      modalDataReturn?.sundayHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        sundayHours: modalDataReturn.sundayHours,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'holiday_hours_updated' &&
      modalDataReturn?.holidayHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        holidayHours: modalDataReturn.holidayHours,
      }));
      didSave = true;
    }
    if (modalDataReturn?.type === 'company_post_it_updated') {
      setCompanyInfo((prevData) => ({
        ...prevData,
        post_it: { ...prevData?.post_it, content: modalDataReturn.postIt },
      }));
      didSave = true;
    }

    // The optimistic setCompanyInfo above keeps the section's own value
    // instant; this refetch is what fills in lastUpdates, since the save
    // endpoints only return the field they touched, not the log entry.
    if (didSave) onSaved?.();

    setModalDataReturn(null);
  }, [modalDataReturn]);

  const editPostIt = () => {
    setModalData({ idCompany: companyInfo?._id });
    toggle('edit_company_post_it');
  };

  return (
    <>
      {/* The note is written from here and nowhere else, so this is the only
          copy carrying a pencil. Adding one is the empty state's job below. */}
      <PostIt
        content={companyInfo?.post_it?.content}
        companyName={companyInfo?.name_commercial ?? companyInfo?.reason_social}
        onEdit={editPostIt}
      />
      {!companyInfo?.post_it?.content?.trim() && (
        <button
          type={'button'}
          // The note's own fill becomes this row's border, over a lighter tint
          // of the same hue: it reads as a post-it waiting to be written, never
          // as one already written — PostIt renders no empty shell of its own.
          className={
            'border border-[#F0E18C] bg-[#FFF3B0] rounded p-3 text-left'
          }
          onClick={editPostIt}
        >
          <div className={'flex justify-between items-center gap-2'}>
            <div>
              <div className={'flex gap-1.5 items-center text-[#5C4D0A]'}>
                <NoteIcon wh={16} color={POST_IT_TITLE_COLOR} />
                <span>Post-it</span>
              </div>
              {/* Darker than the shared gray-500 secondary tone, which only
                  reaches 4.3:1 on this background. */}
              <div className={'text-[#7A6828]'}>
                Une consigne à faire remonter sur les relevés et les contrats
              </div>
            </div>
            <Plus wh={20} color={'#5C4D0A'} />
          </div>
        </button>
      )}
      <button
        type={'button'}
        // Browsers centre a button's text and Tailwind's preflight does not
        // reset it, so the short title drifted while the longer lines below
        // filled the width and looked flush left.
        className={'border rounded p-3 text-left'}
        onClick={() => setIsShowCoeff(true)}
      >
        <div className={'flex justify-between'}>
          <div className={'my-auto'}>
            <div>Coefficient de facturation</div>
            {!isCoeffCompleted && (
              <div className={'text-gray-500 flex gap-2'}>
                <div className={'my-auto'}>
                  <CrossIcon wh={20} color={'#EF4444'} />
                </div>
                <div className={'my-auto'}>La page n'est pas complète</div>
              </div>
            )}
            {renderUpdateStatus(
              'coefficients',
              lastUpdateOf(
                'modification_coefficient_facturation',
                'ajout_coefficient_facturation'
              ),
              'Coefficient appliqué par défaut'
            )}
          </div>
          <div className={'my-auto flex items-center gap-2'}>
            {renderDefaultIcon('coefficients')}
            <ChevronRight wh={24} color={'#111827'} />
          </div>
        </div>
      </button>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasOvertimes && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures supplémentaires</div>
            {hasOvertimes && (
              <div className={''}>
                <div className={'text-gray-500'}>
                  Majoration appliquée lorsqu'un intérimaire travaille :
                </div>
                <ul className={'list-disc list-inside'}>
                  {Object.entries(groupedOvertimes).map(
                    ([increaseKey, groupData]) => {
                      const timeRangesElements = [];
                      const overtimesArray = groupData.items || [];

                      overtimesArray.forEach((overtime, index) => {
                        if (index > 0) {
                          timeRangesElements.push(' et ');
                        }
                        timeRangesElements.push(
                          <span key={index} className={'text-gray-900'}>
                            {overtime.start}h
                          </span>
                        );
                      });

                      const percentageLabel =
                        overtimesArray[0]?.percentage !== undefined &&
                        overtimesArray[0]?.percentage !== null
                          ? `${overtimesArray[0].percentage} %`
                          : 'Non défini';

                      return (
                        <li key={increaseKey} className={''}>
                          <span className={'text-gray-500'}>
                            Plus de {timeRangesElements} sur une semaine :{' '}
                            <span className={'text-gray-900'}>{percentageLabel}</span>
                          </span>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}
            {renderUpdateStatus(
              'overtimes',
              lastUpdateOf('modification_heure_supplementaire', 'ajout_heure_supplementaire'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('overtimes')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editOvertime(companyInfo?._id)}
            >
              {hasOvertimes ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasNightHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures de nuit</div>
            {hasNightHours && (
              <div className={''}>
                <div className={'text-gray-500'}>
                  Majoration appliquée lorsqu'un intérimaire travaille :
                </div>
                <ul className={'list-disc list-inside'}>
                  {Object.entries(groupedNightHours).map(
                    ([increaseKey, groupData]) => {
                      const timeRangesElements = [];
                      const nightHoursArray = groupData.items || [];

                      nightHoursArray.forEach((nightHour, index) => {
                        if (index > 0) {
                          timeRangesElements.push(' et ');
                        }
                        timeRangesElements.push(
                          <span key={index} className={'text-gray-900'}>
                            {nightHour.start}h et {nightHour.end}h
                          </span>
                        );
                      });

                      const percentageLabel =
                        nightHoursArray[0]?.percentage !== undefined &&
                        nightHoursArray[0]?.percentage !== null
                          ? `${nightHoursArray[0].percentage} %`
                          : 'Non défini';

                      return (
                        <li key={increaseKey} className={''}>
                          <span className={'text-gray-500'}>
                            Entre {timeRangesElements} :{' '}
                            <span className={'text-gray-900'}>{percentageLabel}</span>
                          </span>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}
            {renderUpdateStatus(
              'nightHours',
              lastUpdateOf('modification_heure_nuit', 'ajout_heure_nuit'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('nightHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editNightHours(companyInfo?._id)}
            >
              {hasNightHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasSundayHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures du dimanche</div>
            {hasSundayHours && (
              <div className={'text-gray-500'}>
                Majoration appliquée lorsqu'un intérimaire travaille le dimanche
                :{' '}
                <span className={'text-gray-900'}>{sundayPercentageLabel}</span>
              </div>
            )}
            {renderUpdateStatus(
              'sundayHours',
              lastUpdateOf('modification_heure_dimanche', 'ajout_heure_dimanche'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('sundayHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editSundayHours(companyInfo?._id)}
            >
              {hasSundayHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasHolidayHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures jours fériés travaillés</div>
            {hasHolidayHours && (
              <div className={'text-gray-500'}>
                Majoration appliquée lorsqu'un intérimaire lors d'un jour férié
                :{' '}
                <span className={'text-gray-900'}>{holidayPercentageLabel}</span>
              </div>
            )}
            {solidarityDayLabel && (
              <div className={'text-gray-500'}>
                Jour de solidarité :{' '}
                <span className={'text-gray-900'}>{solidarityDayLabel}</span>
              </div>
            )}
            {renderUpdateStatus(
              'holidayHours',
              lastUpdateOf(
                'modification_heure_jour_ferie_travailles',
                'ajout_heure_jour_ferie_travailles'
              ),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('holidayHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editHolidayHours(companyInfo?._id)}
            >
              {hasHolidayHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlideOverCompanyRemunerationConditions;
